import React, { ReactNode, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Button from '../../../components/buttons';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { useStyles } from './styles';
import PhoneTextField from '../../phone-text-field/PhoneTextField';
import { useTranslation } from '../../../hooks/useTranslation';
import { Model, AlertOptions, SmsStopAlertType } from './types';
import { FormState } from '../../../hooks/useFormState.types';
import ManageAlertsControls from './ManageAlertsControls';
import isEqual from 'lodash/isEqual';
import { useIsMobile } from '../../../util/style-utils';
import ROUTES from '../../../routes';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
} from '@material-ui/core';
import AddUsersModal from '../../ssm/post-enrollment/CoCustomer/AddUsersModal';
import AddPerson from './AddPerson';
import {
  displayPhoneNumberFormat,
  parsePhoneNumber,
  toDateString,
} from '../../../util/format';
import { addDays } from 'date-fns';
import PGEButton from '../../buttons/PGE-Button';
import { Close, Delete, Edit } from '@material-ui/icons';
import useSelectedAccountParams from '../../../hooks/useSelectedAccountParams';
import { ApolloQueryResult } from 'apollo-client';
import { LinkedPerson, VerifyLevel } from '../../../__generated__/pge-types';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ManageAlertsConfirmPhoneDialog from './ManageAlertsConfirmPhoneDialog';
import { Link, navigate } from '@reach/router';
import AlertsWarningDialog from './AlertsWarningDialog';
import useAccountCustomer from '../../../hooks/useAccountCustomer';
import Alerts from '../../alerts';
import colors from '../../../themes/main-colors';

type Props = {
  path?: string;
  alerts: Array<AlertOptions>;
  form: FormState<Model>;
  defaultFormValues: Model;
  onSubmit: () => void;
  onPhoneSubmit: (data: Model) => void;
  handleConfirmPhoneSubmit: (
    activationCode: string,
    onInvalid: () => void,
    onSuccess: () => void,
  ) => void;
  personList: any;
  handleChangePerson: (selectedPerson: any) => void;
  selectedPerson: any;
  handleDeletePerson: () => void;
  displayError: string | ReactNode | undefined;
  loggedInPersonId: string | undefined | null;
  refetchPerson: () => Promise<
    ApolloQueryResult<{ getLinkedPersons: LinkedPerson[] }>
  >;
  setRecentlyAddedPerson: React.Dispatch<React.SetStateAction<any>>;
  mainPersonEmail: string | undefined;
  loggedInPersonType: string | undefined | null;
  loggedInPersonName: string | undefined | null;
  onTooManyFailures: () => void;
  updatesAlertsInfo: (phoneNumber: string) => void;
  updateSmsStopPhoneNumber: (phoneNumber: string | undefined) => void;
  updateContactType?: (contactType: string) => void;
  setMobTextField: (singleContact: boolean) => void;
  showMobTextField: boolean;
  selectedContactValue: string | undefined;
  setSelectedContactType: (type: string | undefined) => void;
  setSelectedContactValue: (phoneNumber: string) => void;
  selectedContactType: string | undefined;
  setDropdownInitiallySelected: (flag: boolean) => void;
  isDropdownInitiallySelected: boolean;
  isSmsEnrollmentDisabled: boolean;
  smsStopAlertType?: SmsStopAlertType;
  smsStopNumbers?: string[] | null;
};

export default function ManageAlertsLanding({
  alerts,
  form,
  defaultFormValues,
  onSubmit,
  personList,
  handleChangePerson,
  selectedPerson,
  handleDeletePerson,
  displayError,
  loggedInPersonId,
  refetchPerson,
  setRecentlyAddedPerson,
  mainPersonEmail,
  loggedInPersonType,
  loggedInPersonName,
  onTooManyFailures,
  handleConfirmPhoneSubmit,
  onPhoneSubmit,
  updatesAlertsInfo,
  updateSmsStopPhoneNumber,
  updateContactType,
  setMobTextField,
  showMobTextField,
  selectedContactValue,
  setSelectedContactValue,
  setSelectedContactType,
  selectedContactType,
  setDropdownInitiallySelected,
  isDropdownInitiallySelected,
  isSmsEnrollmentDisabled,
  smsStopAlertType,
  smsStopNumbers,
}: Props) {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();
  const [nextLocation, setNextLocation] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const isFormValueMatched = () => {
    const { phoneNumber, ...updateDefaultFormValues } = defaultFormValues;
    const {
      phoneNumber: formValuePhoneNumber,
      ...updatedFormValues
    } = form.values;
    return isEqual(updateDefaultFormValues, updatedFormValues);
  };

  const handleCancel = () => {
    setShowModal(false);
  };
  const handleNavigate = async (to: string) => {
    if (!isFormValueMatched()) {
      setShowModal(true);
      setNextLocation(to);
    } else {
      await navigate(to || '');
    }
  };
  const handleConfirmLeave = async () => {
    onSubmit();
    setShowModal(false);
    await navigate(nextLocation || '');
  };

  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [showRemovePersonDialog, setShowRemovePersonDialog] = useState(false);
  const [
    showUpdatePhoneNumberDialog,
    setShowUpdatePhoneNumberDialog,
  ] = useState(false);

  const [selectedContactTypeNumbers, setSelectedContactTypeNumbers] = useState<
    string[]
  >([]);

  const [isDropdownValueChanged, setDropdownValueChanged] = useState(false);

  const [
    selectedModifiedContactValue,
    setSelectedModifiedContactValue,
  ] = useState<string | undefined>(undefined);
  const { customer } = useAccountCustomer();
  const getPhoneNumberProps = () => {
    const phoneNumberProps = form.props('phoneNumber');
    return {
      ...phoneNumberProps,
      onChange: async (e: Parameters<typeof phoneNumberProps.onChange>[0]) => {
        const nextValue =
          e && typeof e === 'object' && 'target' in e
            ? e.target.value
            : e && typeof e === 'object' && 'value' in e
            ? e.value
            : e;
        updateSmsStopPhoneNumber(
          nextValue !== undefined ? String(nextValue) : '',
        );
        await phoneNumberProps.onChange(e);
      },
    };
  };
  const getUpdatedPhonenumber = () => {
    if (form.values.phoneNumber?.includes('(')) {
      return form.values.phoneNumber;
    }
    return displayPhoneNumberFormat(form.values.phoneNumber);
  };
  useEffect(() => {
    const contactTypeListData = getContactTypeList();
    const contactTypes = Object.keys(contactTypeListData);
    const contactTypesValues = Object.values(contactTypeListData);
    if (contactTypes && contactTypesValues) {
      const allContactValues = contactTypesValues.flatMap(typeArr =>
        typeArr.map(contact => contact.contactValue),
      );
      const updatedContactValues = allContactValues.filter(
        (value, index, current) => current.indexOf(value) === index,
      );
      setSelectedContactTypeNumbers(updatedContactValues);
    }
    setMobTextField(false);
  }, [customer?.contactDetails]);

  const getContactTypeList = () => {
    return (
      customer?.contactDetails
        ?.filter(
          contact =>
            contact?.contactType === valueMOB ||
            contact?.contactType === valuePNP,
        )
        ?.reduce((acc: Record<string, any[]>, contact) => {
          if (contact && contact.contactType) {
            if (!acc[contact.contactType]) {
              acc[contact.contactType] = [];
            }
            acc[contact.contactType].push(contact);
          }
          return acc;
        }, {}) || {}
    );
  };

  const getContactValueArray = () => {
    const uniqueContactDetails = filterContactDetials?.filter(
      (item, _, arr) =>
        item?.contactType !== 'PNP' ||
        !arr.some(
          other =>
            other?.contactType === 'MOB' &&
            other.contactValue === item?.contactValue,
        ),
    );
    return (
      uniqueContactDetails?.map(contact => ({
        contactValue: contact?.contactValue,
        encryptedContactId: contact?.encryptedContactId,
        contactType: contact?.contactType,
      })) || []
    );
  };
  const getContactTypeFromValue = (contactValue: string) => {
    const valueToRemove = 'EMAIL';
    const contact = customer?.contactDetails?.filter(
      c => c?.contactType !== valueToRemove,
    );
    const matchedContact = contact?.find(c => c?.contactValue === contactValue);
    const initialContact = contact?.find(
      c =>
        c?.contactValue ===
        displayPhoneNumberFormat(defaultFormValues.phoneNumber),
    );

    switch (selectedContactType || initialContact?.contactType) {
      case 'MOB':
        return t('MOBILE_PHONE');
      case 'PNP':
        return t('PRIMARY_PHONE_MANAGE_ALERTS');
      default:
        return t('MOBILE_PHONE');
    }
  };

  const { accountParams } = useSelectedAccountParams();
  const onActivatePhone = (activationCode: string, onInvalid: () => void) => {
    handleConfirmPhoneSubmit(activationCode, onInvalid, () => {
      setShowUpdatePhoneNumberDialog(false);
    });
  };
  const handleCloseUpdatePhoneNumberDialog = () => {
    const phoneNumber = selectedContactValue
      ? parsePhoneNumber(selectedContactValue)
      : defaultFormValues.phoneNumber;
    void form.setValue('phoneNumber', phoneNumber);
    updateSmsStopPhoneNumber(
      selectedContactValue || defaultFormValues.phoneNumber,
    );
    setDropdownValueChanged(false);
    setMobTextField(false);
    setShowUpdatePhoneNumberDialog(false);
  };
  const valueMOB = 'MOB';
  const valuePNP = 'PNP';
  const contactDetails = [...(customer?.contactDetails || [])];
  const filterContactDetials = contactDetails?.filter(
    contact =>
      contact?.contactType === valueMOB || contact?.contactType === valuePNP,
  );

  const formatedContactDetails = filterContactDetials?.find(con => {
    return (
      con?.contactType === selectedContactType &&
      con?.contactValue === displayPhoneNumberFormat(form.values.phoneNumber)
    );
  });
  const isPhoneNumbersDdifferent = () => {
    const defaultPphoneContactType = filterContactDetials?.find(
      con =>
        con?.contactValue ===
        displayPhoneNumberFormat(defaultFormValues.phoneNumber),
    )?.contactType;
    const isContactTypeMatched =
      selectedContactType === defaultPphoneContactType;
    return isContactTypeMatched;
  };
  const isUpdateButtonDisabled = () => {
    if (!isDropdownValueChanged) {
      if (formatedContactDetails) {
        return true;
      }
      if (
        selectedContactTypeNumbers?.length > 1 &&
        showMobTextField &&
        !isEqual(form.values.phoneNumber, defaultFormValues.phoneNumber)
      ) {
        return (
          isEqual(
            form.values.phoneNumber,
            parsePhoneNumber(selectedContactValue || ''),
          ) ||
          form.errors.phoneNumber === t('MANAGE_ALERTS_BAD_PHONE_NUMBER') ||
          form.errors.phoneNumber === 'Enter a phone number.' ||
          form.values.phoneNumber === ''
        );
      } else if (!showMobTextField) {
        if (selectedContactTypeNumbers?.length > 1 && selectedContactValue) {
          return (
            isEqual(
              form.values.phoneNumber,
              parsePhoneNumber(selectedContactValue || ''),
            ) ||
            form.errors.phoneNumber === t('MANAGE_ALERTS_BAD_PHONE_NUMBER') ||
            form.errors.phoneNumber === 'Enter a phone number.' ||
            form.values.phoneNumber === ''
          );
        } else {
          return (
            isEqual(defaultFormValues.phoneNumber, form.values.phoneNumber) ||
            form.errors.phoneNumber === t('MANAGE_ALERTS_BAD_PHONE_NUMBER') ||
            form.values.phoneNumber === ''
          );
        }
      } else {
        if (!isPhoneNumbersDdifferent()) {
          return false;
        }
        return (
          isEqual(defaultFormValues.phoneNumber, form.values.phoneNumber) ||
          form.errors.phoneNumber === t('MANAGE_ALERTS_BAD_PHONE_NUMBER') ||
          form.values.phoneNumber === ''
        );
      }
    }
    return true;
  };

  return (
    <Grid container spacing={2}>
      <Grid item>
        <Typography>{t('MANAGE_ALERT_SUB_CONTENT')}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant={'h2'} className={classes.colorTextPrimary}>
              {t('MANAGE_ACCOUNT_USERS')}
            </Typography>

            <Typography variant={'body1'} className={classes.colorTextPrimary}>
              {t('MANAGE_ALERT_ADD_PEOPLE_TEXT')}
            </Typography>
            {selectedPerson && selectedPerson.verificationStatus === 'Pending' && (
              <Typography
                variant={'subtitle1'}
                className={classes.colorTextPrimary}
              >
                {richT('EMAIL_SENT_TEXT', {
                  PERSON_NAME: selectedPerson.name,
                  START_DATE: toDateString(
                    JSON.parse(selectedPerson.inputData!).createdOn,
                    'MM/dd/yyyy',
                  ),
                  END_DATE: toDateString(
                    addDays(
                      new Date(JSON.parse(selectedPerson.inputData!).createdOn),
                      3,
                    ),
                    'MM/dd/yyyy',
                  ),
                })}
              </Typography>
            )}
            <Box>
              <Select
                labelId="account-select-label"
                id="account-select-select"
                value={selectedPerson}
                variant="outlined"
                onChange={async e => await handleChangePerson(e.target.value)}
                className={classes.select}
                placeholder={t('SELECT_PERSON')}
              >
                {personList &&
                  personList?.map((v: any) => (
                    <MenuItem key={v.encryptedPersonId} value={v}>
                      {v.verificationStatus === 'Pending'
                        ? v.name + t('PENDING_VERIFICATION')
                        : v.name}
                    </MenuItem>
                  ))}
              </Select>
            </Box>

            {displayError && <Typography>{displayError}</Typography>}
            {loggedInPersonType !== 'AUTHUSR' && (
              <Grid container item justify={isMobile ? 'center' : 'flex-end'}>
                <Grid item xs={10} md="auto" className={classes.buttonBox}>
                  <PGEButton
                    size={'large'}
                    color={'primary'}
                    variant={'contained'}
                    className={classes.deletePerson}
                    onClick={() => setShowRemovePersonDialog(true)}
                    data-testid="done"
                    disabled={
                      selectedPerson?.personId === loggedInPersonId ||
                      selectedPerson?.verificationStatus === 'Pending'
                    }
                  >
                    <Delete fontSize="small" className={classes.deleteIcon} />
                    {t('REMOVE_USER')}
                  </PGEButton>
                  <PGEButton
                    size={'large'}
                    color={'primary'}
                    variant={'contained'}
                    className={classes.addPerson}
                    onClick={() => setShowAddPersonModal(true)}
                    data-testid="done"
                  >
                    {t('ADD_USER')}
                  </PGEButton>
                </Grid>
              </Grid>
            )}

            <Accordion
              defaultExpanded
              className={classes.userContactDetailsAccordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant={'h2'} className={classes.colorTextPrimary}>
                  {t('USER_CONTACT_SETTINGS')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Typography variant={'body1'}>
                  {t('PHONE_EMAIL_TEXT_UPDATED')}
                </Typography>
                <Typography className={classes.emailAndPhoneLabel}>
                  {t('EMAIL_LABEL')}
                </Typography>
                <Box className={classes.userContact}>
                  <Typography variant={'body1'}>
                    {t('MANAGE_USER_EMAIL_WARNING')}{' '}
                    <Link
                      to={ROUTES.CHANGE_EMAIL}
                      onClick={e => {
                        void handleNavigate(ROUTES.CHANGE_EMAIL);
                        e.preventDefault();
                      }}
                    >
                      {t('CHANGE_EMAIL').toLowerCase()}
                    </Link>
                    {' ' + t('PAGE').toLowerCase() + '.'}
                  </Typography>
                  <div className={classes.primaryEmail}>
                    {selectedPerson && (
                      <>
                        <Typography variant={'body1'}>{`${t(
                          'PRIMARY',
                        )}:`}</Typography>
                        <Typography
                          variant={'body1'}
                          className={classes.primaryEmailStyles}
                        >
                          {form.values.email}
                        </Typography>
                      </>
                    )}
                  </div>
                </Box>
                <Typography className={classes.emailAndPhoneLabel}>
                  {t('CONTACT_NUMBER')}
                </Typography>
                <Box className={classes.userContact}>
                  {selectedContactTypeNumbers.length > 1 && (
                    <Typography
                      style={{
                        fontSize: '16px',
                        marginBottom: '5px',
                        justifyContent: 'space-between',
                      }}
                    >
                      {t('SELECT_CONTACT_NUMBER')}
                    </Typography>
                  )}
                  <Grid container spacing={1}>
                    <Grid
                      item
                      xs={isMobile ? 4 : 3}
                      style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography>
                        {getContactTypeFromValue(getUpdatedPhonenumber())}
                      </Typography>
                    </Grid>
                    <Grid item xs={isMobile ? 8 : 9}>
                      {selectedContactTypeNumbers?.length <= 1 &&
                        ((selectedPerson &&
                          !selectedPerson.isCssUser &&
                          selectedPerson.verificationStatus === 'Verified') ||
                          (selectedPerson && selectedPerson.personId) ===
                            loggedInPersonId) && (
                          <PhoneTextField
                            label={''}
                            style={{ width: '100%' }}
                            {...getPhoneNumberProps()}
                          />
                        )}
                      {selectedContactTypeNumbers?.length > 1 &&
                        ((selectedPerson &&
                          !selectedPerson.isCssUser &&
                          selectedPerson.verificationStatus ===
                            VerifyLevel.Verified) ||
                          (selectedPerson && selectedPerson.personId) ===
                            loggedInPersonId) && (
                          <FormControl
                            className={classes.mobNumberForm}
                            variant="outlined"
                          >
                            {!showMobTextField && (
                              <Grid item xs={isMobile ? 9 : 11}>
                                <Select
                                  style={{ width: '100%' }}
                                  value={
                                    getContactValueArray().find(
                                      con =>
                                        (con.contactValue ===
                                          selectedContactValue &&
                                          con.contactType ===
                                            selectedContactType) ||
                                        (con.contactType ===
                                          selectedContactType &&
                                          con.contactValue ===
                                            getUpdatedPhonenumber()),
                                    )?.encryptedContactId ||
                                    getContactValueArray().find(
                                      con =>
                                        con.contactValue ===
                                        getUpdatedPhonenumber(),
                                    )?.encryptedContactId
                                  }
                                  onChange={async e => {
                                    const contactValue = getContactValueArray().filter(
                                      con =>
                                        con.encryptedContactId ===
                                        e.target.value,
                                    )[0].contactValue;
                                    const sele = parsePhoneNumber(
                                      String(contactValue),
                                    );
                                    void (await form.setValue(
                                      'phoneNumber',
                                      sele,
                                    ));
                                    setDropdownValueChanged(true);
                                    setSelectedContactValue(
                                      String(contactValue),
                                    );
                                    const matchedContactType = customer?.contactDetails?.filter(
                                      con =>
                                        con?.encryptedContactId ===
                                        e.target.value,
                                    )[0]?.contactType;
                                    updateContactType &&
                                      matchedContactType &&
                                      updateContactType(matchedContactType);
                                    setSelectedContactType(matchedContactType);
                                    updatesAlertsInfo(String(contactValue));
                                    updateSmsStopPhoneNumber(
                                      String(contactValue),
                                    );
                                    !isDropdownInitiallySelected &&
                                      setDropdownInitiallySelected(true);
                                  }}
                                >
                                  {getContactValueArray().map(
                                    (mob: any | undefined) =>
                                      mob ? (
                                        <MenuItem
                                          key={mob?.encryptedContactId}
                                          value={mob?.encryptedContactId}
                                        >
                                          {mob.contactValue}
                                        </MenuItem>
                                      ) : null,
                                  )}
                                </Select>
                              </Grid>
                            )}
                            {showMobTextField && (
                              <Grid item xs={isMobile ? 9 : 10}>
                                <PhoneTextField
                                  label={t('EDIT_CONTACT_NUMBER')}
                                  style={{ width: '100%', margin: 0 }}
                                  {...getPhoneNumberProps()}
                                />
                              </Grid>
                            )}
                            <Grid item xs={isMobile ? 3 : 2}>
                              <PGEButton
                                size="large"
                                variant="contained"
                                className={classes.mobNumberStyles}
                                onClick={() => {
                                  if (showMobTextField) {
                                    if (
                                      form.values.phoneNumber !==
                                      parsePhoneNumber(
                                        selectedContactValue || '',
                                      )
                                    ) {
                                      void form.setValue(
                                        'phoneNumber',
                                        selectedContactValue
                                          ? parsePhoneNumber(
                                              selectedContactValue || '',
                                            )
                                          : defaultFormValues.phoneNumber,
                                      );
                                      updateSmsStopPhoneNumber(
                                        selectedContactValue ||
                                          defaultFormValues.phoneNumber,
                                      );
                                    } else {
                                      void form.setValue(
                                        'phoneNumber',
                                        selectedContactValue
                                          ? parsePhoneNumber(
                                              selectedContactValue || '',
                                            )
                                          : form.values.phoneNumber,
                                      );
                                      updateSmsStopPhoneNumber(
                                        selectedContactValue ||
                                          form.values.phoneNumber,
                                      );
                                    }
                                    setSelectedModifiedContactValue(undefined);
                                  }
                                  setDropdownValueChanged(false);
                                  setMobTextField(!showMobTextField);
                                }}
                              >
                                {showMobTextField ? (
                                  <IconButton
                                    title={t('CLOSE')}
                                    color="inherit"
                                  >
                                    <Close />
                                  </IconButton>
                                ) : (
                                  <IconButton
                                    title={t('EDIT_CONTACT_NUMBER')}
                                    color="inherit"
                                  >
                                    <Edit />
                                  </IconButton>
                                )}
                              </PGEButton>
                            </Grid>
                          </FormControl>
                        )}
                    </Grid>
                    {selectedPerson &&
                      ((selectedPerson.isCssUser &&
                        selectedPerson.personId !== loggedInPersonId) ||
                        selectedPerson.verificationStatus === 'Pending') && (
                        <>
                          <Typography variant={'body1'}>{`${t(
                            'Phone',
                          )}:`}</Typography>
                          <Typography
                            variant={'body1'}
                            className={classes.boldText}
                          >
                            {displayPhoneNumberFormat(form.values.phoneNumber)}
                          </Typography>
                        </>
                      )}
                  </Grid>
                  {!form.values.phoneNumber && (
                    <div>
                      <Typography variant="subtitle1">
                        {t('SMS_NOTIFICATION_WARNING')}
                      </Typography>
                    </div>
                  )}
                  {smsStopAlertType === 'blocked' && (
                    <Box
                      mt={2}
                      css={{
                        '& .MuiAlert-root': {
                          backgroundColor: colors.white,
                          color: colors.noirBlur,
                        },
                        '& .MuiAlertTitle-root': {
                          fontWeight: 'bold',
                        },
                        '& .MuiAlert-message > ol': {
                          marginBlockStart: 0,
                        },
                      }}
                    >
                      <Alerts
                        disableColorOverride={true}
                        isOpen
                        severity="warning"
                        title={t('SMS_ALERT_STOP_BLOCKED_TITLE')}
                        message={richT('SMS_ALERT_STOP_UNBLOCK_INSTRUCTIONS', {
                          SMS_SHORT_CODE: (smsStopNumbers ?? []).join(', '),
                        })}
                      />
                    </Box>
                  )}
                </Box>
                <div className={classes.updateButtonContainer}>
                  <PGEButton
                    size="large"
                    className={classes.updateButton}
                    onClick={() => {
                      onPhoneSubmit(form.values);
                      setShowUpdatePhoneNumberDialog(true);
                    }}
                    disabled={isUpdateButtonDisabled()}
                  >
                    {t('UPDATE')}
                  </PGEButton>
                </div>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      </Grid>
      <Grid item>
        <Card>
          <CardContent className={classes.alertsCard}>
            {form.values.email &&
              form.values.email !== defaultFormValues.email &&
              form.values.phoneNumber &&
              form.values.phoneNumber !== defaultFormValues.phoneNumber && (
                <>
                  <Typography
                    color={'textPrimary'}
                    className={classes.colorTextPrimary}
                    variant={'h4'}
                    style={{ marginTop: 20, width: '100%' }}
                  >
                    {t('CHOOSE_VERIFICATION')}
                  </Typography>
                </>
              )}
            <Box>
              <ManageAlertsControls
                form={form}
                alerts={alerts}
                handleNavigate={handleNavigate}
                isSmsEnrollmentDisabled={isSmsEnrollmentDisabled}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid container item justify={isMobile ? 'center' : 'flex-end'}>
        <Grid item xs={10} md="auto">
          <PGEButton
            className={classes.savePreferencesButton}
            size={'large'}
            disabled={isFormValueMatched() || form.values.email === ''}
            onClick={() => onSubmit()}
            fullWidth
          >
            {t('SAVE_PREFERENCES')}
          </PGEButton>
        </Grid>
      </Grid>
      {showAddPersonModal && (
        <Grid>
          <AddUsersModal
            onClose={() => setShowAddPersonModal(false)}
            open={showAddPersonModal}
            title={t('ADD_PERSON_MODEL_TITLE')}
            isDynamicClass={true}
            mainTitle={t('ADD_AN_ACCOUNT_USER')}
          >
            <AddPerson
              setShowAddPersonModal={setShowAddPersonModal}
              refetchPerson={refetchPerson}
              setRecentlyAddedPerson={setRecentlyAddedPerson}
              mainPersonEmail={mainPersonEmail}
              loggedInPerson={loggedInPersonId}
              loggedInPersonName={loggedInPersonName}
            />
          </AddUsersModal>
        </Grid>
      )}
      {showRemovePersonDialog && (
        <Dialog
          open={showRemovePersonDialog}
          onClose={() => setShowRemovePersonDialog(false)}
          scroll={'body'}
          aria-labelledby="delete-person-modal"
          aria-describedby="delete-person-modal"
          className={classes.removePersonDataModalWrapper}
          disableScrollLock
        >
          <Paper className={classes.removePersonDataModal}>
            <Box>
              <Grid container justify="space-between">
                <Typography
                  variant={'body1'}
                  gutterBottom
                  style={{ paddingRight: '24px' }}
                >
                  {''}
                </Typography>

                <IconButton
                  aria-label="close"
                  color="inherit"
                  onClick={() => setShowRemovePersonDialog(false)}
                  className={classes.closeBtn}
                >
                  <Close />
                </IconButton>
              </Grid>

              <Box className={classes.modalBody}>
                <Typography variant={'h3'}>
                  {richT('REMOVE_PERSON_CONFIRMATION_TEXT', {
                    PERSON_NAME: selectedPerson.name ?? '',
                    ACCOUNT_NUMBER: accountParams?.accountNumber,
                  })}
                </Typography>

                <Grid style={{ marginTop: 20 }} container spacing={2}>
                  <Grid className={classes.dialogBtn} item xs={12} md={6}>
                    <PGEButton
                      size="large"
                      color="secondary"
                      variant="outlined"
                      onClick={() => setShowRemovePersonDialog(false)}
                    >
                      {t('CANCEL')}
                    </PGEButton>
                  </Grid>
                  <Grid item xs={12} md={6} className={classes.confirmBtn}>
                    <PGEButton
                      size="large"
                      onClick={async () => {
                        handleDeletePerson();
                        setShowRemovePersonDialog(false);
                      }}
                    >
                      {t('CONFIRM')}
                    </PGEButton>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Paper>
        </Dialog>
      )}
      {showUpdatePhoneNumberDialog && (
        <ManageAlertsConfirmPhoneDialog
          onClose={handleCloseUpdatePhoneNumberDialog}
          phoneNumber={form.values.phoneNumber}
          onSubmit={onActivatePhone}
          hasActivationCode={true}
          onTooManyFailures={onTooManyFailures}
        />
      )}
      {showModal && (
        <Dialog open={showModal}>
          <AlertsWarningDialog
            confirm={handleConfirmLeave}
            cancel={handleCancel}
          />
        </Dialog>
      )}
    </Grid>
  );
}
